/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef } from "react";
import { useSession } from "../../context/AuthContext";
import { useToast } from "../providers/ToastProvider";
import api from "../../services/api";

interface Comment {
  id: string;
  submissionId: string;
  authorId: string;
  content: string;
  parentId: string | null;
  createdAt: string;
  created_at?: string;
  author: {
    name: string;
    role: string;
  };
}

interface CommentSectionProps {
  submissionId: string;
  hideHeader?: boolean;
  welcomeMessage?: string;
  welcomeMessages?: { role: 'student' | 'coach', text: string }[];
}

export default function CommentSection({ submissionId, hideHeader = false, welcomeMessage, welcomeMessages }: CommentSectionProps) {
  const { data: session }: any = useSession();
  const userId = session?.user?.id;
  const { showToast } = useToast();

  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const fetchComments = async () => {
    if (!submissionId) return;
    try {
      const res = await api.get(`/comments/${submissionId}?readerId=${userId}&t=${Date.now()}`);
      setComments(res.data);
      // Trigger global unread count refresh with a tiny delay to ensure DB sync
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("discussionRead"));
      }, 100);
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [submissionId, userId]);

  useEffect(() => {
    if (replyTo && inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyTo]);

  const handlePostComment = async (content: string, parentId: string | null = null) => {
    if (!content.trim() || !userId) return;
    setIsSubmitting(true);
    try {
      const res = await api.post(`/comments`, {
        submissionId,
        authorId: userId,
        content: content.trim(),
        parentId: parentId || replyTo
      });

      const posted = res.data;
      setComments([...comments, posted]);
      setNewComment("");
      setReplyTo(null);
    } catch (err) {
      showToast("Error", "Failed to post comment.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group comments into threads
  const rootComments = [...comments].filter(c => !c.parentId).reverse(); // Latest on top
  const getReplies = (parentId: string) => comments.filter(c => c.parentId === parentId);

  return (
    <div className="space-y-6 flex flex-col h-full">
      {!hideHeader && (
        <div className="flex items-center justify-between pb-4 border-b border-outline-variant/10">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-sm text-primary">forum</span>
            <h4 className="text-sm font-black text-slate-950 tracking-tight">Comments</h4>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/40">{comments.length} Comments</span>
        </div>
      )}

      {/* Comment Feed */}
      <div className="flex flex-col-reverse gap-6 overflow-y-auto pr-2 custom-scrollbar flex-1">
        {rootComments.length > 0 ? (
          <>
            {rootComments.map((comment) => (
              <div key={comment.id} id={`msg-${comment.id}`} className="flex flex-col space-y-2 scroll-mt-20">
                <CommentItem
                  comment={comment}
                  isReply={false}
                  onReply={() => setReplyTo(comment.id)}
                  currentUserId={userId}
                  comments={comments}
                />

                {/* Replies */}
                {getReplies(comment.id).length > 0 && (
                  <div className="flex flex-col space-y-2">
                    {getReplies(comment.id).map(reply => (
                      <div key={reply.id} id={`msg-${reply.id}`} className="scroll-mt-20">
                        <CommentItem
                          comment={reply}
                          isReply={true}
                          onReply={() => { }} // Deep nesting disabled for UI simplicity
                          currentUserId={userId}
                          comments={comments}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {(welcomeMessages || (welcomeMessage ? [{ role: 'coach' as const, text: welcomeMessage }] : [])).length > 0 && (
              <div className="flex flex-col items-start w-full pt-4 space-y-4 relative mt-4">
                {(welcomeMessages || (welcomeMessage ? [{ role: 'coach' as const, text: welcomeMessage }] : [])).map((msg, idx) => {
                  const isStudent = msg.role === 'student';
                  return (
                    <div key={idx} className={`w-full flex ${isStudent ? 'justify-end' : 'justify-start relative'}`}>
                      {!isStudent && idx === 0 && (
                        <div className="absolute -top-6 left-0 flex items-center gap-1.5 opacity-40">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          <span className="text-[8px] font-black uppercase tracking-widest text-primary">Support Team</span>
                        </div>
                      )}
                      <div className={`group relative max-w-[85%] sm:max-w-[75%] p-4 border ${
                        isStudent 
                          ? 'bg-on-surface text-surface border-transparent rounded-[1.75rem] rounded-tr-[0.25rem] shadow-xl' 
                          : 'bg-surface-container-high/60 backdrop-blur-sm text-on-surface border-outline-variant/10 rounded-[1.75rem] rounded-tl-[0.25rem]'
                      }`}>
                        <p className={`text-[13px] font-medium leading-snug whitespace-pre-wrap ${isStudent ? 'text-surface' : 'text-on-surface'}`}>
                          {msg.text}
                        </p>
                        <div className={`flex items-center gap-1.5 mt-2 ${isStudent ? 'justify-end' : 'justify-start'}`}>
                          <span className={`text-[8px] font-black uppercase tracking-widest opacity-40 ${isStudent ? 'text-surface' : 'text-on-surface'}`}>
                            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          (welcomeMessages || welcomeMessage) ? (
            <div className="flex flex-col items-start w-full pt-4 space-y-4 relative mt-4">
              {(welcomeMessages || (welcomeMessage ? [{ role: 'coach' as const, text: welcomeMessage }] : [])).map((msg, idx) => {
                const isStudent = msg.role === 'student';
                return (
                  <div key={idx} className={`w-full flex ${isStudent ? 'justify-end' : 'justify-start relative'}`}>
                    {!isStudent && idx === 0 && (
                      <div className="absolute -top-6 left-0 flex items-center gap-1.5 opacity-40">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        <span className="text-[8px] font-black uppercase tracking-widest text-primary">Support Team</span>
                      </div>
                    )}
                    <div className={`group relative max-w-[85%] sm:max-w-[75%] p-4 border ${
                      isStudent 
                        ? 'bg-on-surface text-surface border-transparent rounded-[1.75rem] rounded-tr-[0.25rem] shadow-xl' 
                        : 'bg-surface-container-high/60 backdrop-blur-sm text-on-surface border-outline-variant/10 rounded-[1.75rem] rounded-tl-[0.25rem]'
                    }`}>
                      <p className={`text-[13px] font-medium leading-snug whitespace-pre-wrap ${isStudent ? 'text-surface' : 'text-on-surface'}`}>
                        {msg.text}
                      </p>
                      <div className={`flex items-center gap-1.5 mt-2 ${isStudent ? 'justify-end' : 'justify-start'}`}>
                        <span className={`text-[8px] font-black uppercase tracking-widest opacity-40 ${isStudent ? 'text-surface' : 'text-on-surface'}`}>
                          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-10 text-center space-y-2 opacity-30">
              <span className="material-symbols-outlined text-4xl">forum</span>
              <p className="text-[10px] font-black uppercase tracking-widest">No comments yet</p>
            </div>
          )
        )}
      </div>

      {/* Main Input - At Bottom */}
      <div className="relative pt-4 border-t border-outline-variant/10 px-1">
        {/* WhatsApp-style Reply Preview */}
        {replyTo && (
          <div className="mb-3 p-4 bg-surface-container-low rounded-2xl border-l-4 border-primary animate-in slide-in-from-bottom-2 duration-200 flex items-center justify-between">
            <div className="overflow-hidden">
              <p className="text-[9px] font-black uppercase tracking-widest text-primary mb-1">
                Replying to {comments.find(c => c.id === replyTo)?.author.name}
              </p>
              <p className="text-xs text-on-surface-variant truncate opacity-60">
                {comments.find(c => c.id === replyTo)?.content}
              </p>
            </div>
            <button
              onClick={() => setReplyTo(null)}
              className="p-1 hover:bg-surface-container-high rounded-full transition-colors ml-4"
            >
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          </div>
        )}

        <div className="relative">
          <textarea
            ref={inputRef}
            placeholder={replyTo ? "Write a reply..." : "Write a comment..."}
            className="w-full px-6 py-5 bg-surface-container-highest/30 border border-outline-variant/10 rounded-[1.75rem] outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm font-medium resize-none min-h-[100px] text-on-surface"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
          <button
            onClick={() => handlePostComment(newComment, replyTo)}
            disabled={isSubmitting || !newComment.trim()}
            className="absolute right-4 bottom-4 p-3 bg-on-surface text-surface rounded-2xl hover:translate-y-[-2px] hover:shadow-lg transition-all active:scale-95 disabled:opacity-30 disabled:translate-y-0 shadow-xl"
          >
            <span className="material-symbols-outlined text-base">send</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentItem({
  comment,
  isReply,
  onReply,
  currentUserId,
  comments = []
}: any) {
  const isMine = comment.authorId === currentUserId;
  const isStaff = comment.author.role !== "STUDENT";
  const parentComment = comment.parentId ? comments.find((c: any) => c.id === comment.parentId) : null;

  const scrollToOriginal = (e: React.MouseEvent) => {
    if (!parentComment) return;
    e.stopPropagation();
    const element = document.getElementById(`msg-${parentComment.id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a brief highlight effect
      element.classList.add('bg-primary/5', 'rounded-2xl');
      setTimeout(() => {
        element.classList.remove('bg-primary/5', 'rounded-2xl');
      }, 2000);
    }
  };

  return (
    <div className={`flex flex-col ${isMine ? "items-end" : "items-start"} w-full`}>
      <div className={`group relative max-w-[85%] sm:max-w-[75%] p-4 rounded-[1.75rem] transition-all ${isMine
          ? "bg-on-surface text-surface rounded-tr-[0.25rem] shadow-lg"
          : "bg-surface-container-high/60 backdrop-blur-sm text-on-surface border border-outline-variant/10 rounded-tl-[0.25rem]"
        } ${isReply ? "scale-95 opacity-90" : ""}`}>

        {/* Quote Block for Replies */}
        {parentComment && (
          <div
            onClick={scrollToOriginal}
            className={`mb-3 p-3 rounded-xl border-l-4 cursor-pointer hover:opacity-80 transition-all overflow-hidden ${isMine
                ? "bg-white/10 border-white/30"
                : "bg-black/5 border-primary/40"
              }`}
          >
            <p className={`text-[11px] truncate opacity-60 ${isMine ? "text-white" : "text-on-surface"}`}>
              {parentComment.content}
            </p>
          </div>
        )}

        <p className={`text-[13px] font-medium leading-snug whitespace-pre-wrap ${isMine ? "text-surface" : "text-on-surface"}`}>
          {comment.content}
        </p>

        <div className={`flex items-center gap-1.5 mt-2 ${isMine ? "justify-end" : "justify-start"}`}>
          <span className={`text-[8px] font-black uppercase tracking-widest opacity-40 ${isMine ? "text-surface" : "text-on-surface"}`}>
            {new Date(comment.createdAt || comment.created_at || new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          {isMine && (
            <span 
              className={`material-symbols-outlined font-bold select-none leading-none -ml-0.5 ${
                comment.read ? "text-sky-400" : "text-surface/40"
              }`}
              style={{ fontSize: '12px', width: '12px', height: '12px', display: 'inline-block' }}
              title={comment.read ? "Read" : "Delivered"}
            >
              done_all
            </span>
          )}
          {!isReply && onReply && (
            <button
              onClick={onReply}
              className={`text-[8px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all ${isMine ? "text-surface/60 hover:text-surface" : "text-primary/60 hover:text-primary"
                }`}
            >
              Reply
            </button>
          )}
        </div>

        {/* Status indicator if staff */}
        {isStaff && !isMine && (
          <div className="absolute -top-6 left-0 flex items-center gap-1.5 opacity-40 group-hover:opacity-100 transition-opacity">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-[8px] font-black uppercase tracking-widest text-primary">Staff Reply</span>
          </div>
        )}
      </div>
    </div>
  );
}
